import React, { useMemo, useState } from 'react';
import type { Partner, PartnerCalendarEntry, PartnerCalendarEntryKind, PartnerLocation } from '../types';
import type { Squad } from '../../../types';
import SquadCalendar, { type AuxCalendarEvent } from '../../squads/components/SquadCalendar';
import {
  createPartnerCalendarEntry,
  deletePartnerCalendarEntry,
  updatePartnerCalendarEntry,
} from '../services/partnerRepository';
import { syncSquadCalendarFromDetails } from '../../squads/utils/calendarSync';
import { effectiveScheduleDate } from '../../squads/utils/singleDaySchedule';
import { expandEntryDates, squadInheritsNoClassDays, ymdAddDays } from '../utils/partnerCalendarEntries';
import { createDateInZone, DEFAULT_TZ } from '../../../shared/utils/timezone';

export type ConvertEventOptions = {
  title: string;
  locationId: string;
  singleDayType: 'class' | 'demo';
  demoType?: Squad['demoType'];
  startTime: string;
  endTime: string;
};

const YMD_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function dateKeyInTimeZone(date: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value || '';
  return `${value('year')}-${value('month')}-${value('day')}`;
}

function formatEntryDate(ymd: string): string {
  if (!YMD_PATTERN.test(String(ymd || ''))) return String(ymd || '');
  const [y, m, d] = ymd.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

function formatEntryDateRange(entry: PartnerCalendarEntry): string {
  const start = formatEntryDate(entry.startDate);
  const end = entry.endDate && entry.endDate !== entry.startDate ? formatEntryDate(entry.endDate) : '';
  return end ? `${start} – ${end}` : start;
}

function formatClock(value: string | null | undefined): string {
  const match = String(value || '').match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return '';
  const hour = Number(match[1]);
  if (hour > 23) return '';
  return `${hour % 12 || 12}:${match[2]} ${hour >= 12 ? 'PM' : 'AM'}`;
}

type EntryFormState = {
  id: string | null;
  kind: PartnerCalendarEntryKind;
  title: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  locationId: string;
  excludedLocationIds: string[];
  notes: string;
};

function emptyForm(kind: PartnerCalendarEntryKind): EntryFormState {
  return {
    id: null,
    kind,
    title: '',
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    locationId: '',
    excludedLocationIds: [],
    notes: '',
  };
}

function formFromEntry(entry: PartnerCalendarEntry): EntryFormState {
  return {
    id: entry.id,
    kind: entry.kind,
    title: entry.title || '',
    startDate: entry.startDate || '',
    endDate: entry.endDate && entry.endDate !== entry.startDate ? entry.endDate : '',
    startTime: entry.startTime || '',
    endTime: entry.endTime || '',
    locationId: entry.locationId || '',
    excludedLocationIds: Array.isArray(entry.excludedLocationIds) ? entry.excludedLocationIds : [],
    notes: entry.notes || '',
  };
}

export default function PartnerCalendarTab(props: {
  partner: Partner;
  locations: PartnerLocation[];
  squads: Squad[];
  entries: PartnerCalendarEntry[];
  actorId: string | null;
  onChanged: () => Promise<void>;
  onOpenSquad: (squadId: string) => void;
  onConvertEventToSquad: (entry: PartnerCalendarEntry, options: ConvertEventOptions) => Promise<string>;
}): React.ReactElement {
  const { partner, locations, squads, entries, actorId, onChanged, onOpenSquad, onConvertEventToSquad } = props;
  const partnerTz = partner.timezone || DEFAULT_TZ;
  const [form, setForm] = useState<EntryFormState | null>(null);
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);
  const [converting, setConverting] = useState(false);
  const [convertDraft, setConvertDraft] = useState<ConvertEventOptions | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);

  const currentDate = dateKeyInTimeZone(new Date(), partnerTz);
  const futureSquads = useMemo(() => squads.filter((squad) => {
    const isCanceled = ['canceled', 'cancelled'].includes(String(squad.status || '').toLowerCase());
    const lastDay = effectiveScheduleDate(squad, 'end');
    return !isCanceled && lastDay >= currentDate;
  }), [currentDate, squads]);

  const selectedEntry = useMemo(
    () => entries.find((entry) => entry.id === selectedEntryId) || null,
    [entries, selectedEntryId],
  );

  const locationName = (locationId: string | null | undefined) =>
    locations.find((item) => item.id === locationId)?.name || '';

  const auxEvents = useMemo<AuxCalendarEvent[]>(() => entries.map((entry) => {
    const kind = entry.kind === 'no_class' ? 'no_class' as const : 'partner_event' as const;
    const hasTimes = entry.kind === 'event' && entry.startTime && entry.endTime;
    if (!hasTimes) {
      const dates = expandEntryDates(entry);
      const lastDate = dates[dates.length - 1] || entry.startDate;
      return {
        id: entry.id,
        title: entry.title,
        kind,
        start: entry.startDate,
        end: ymdAddDays(lastDate, 1), // all-day ends are exclusive
        allDay: true,
        converted: Boolean(entry.convertedSquadId),
        source: entry.source,
      };
    }
    const tz = locations.find((item) => item.id === entry.locationId)?.timezone || partnerTz;
    return {
      id: entry.id,
      title: entry.title,
      kind,
      start: createDateInZone(entry.startDate, entry.startTime as string, tz),
      end: createDateInZone(entry.endDate || entry.startDate, entry.endTime as string, tz),
      allDay: false,
      converted: Boolean(entry.convertedSquadId),
      source: entry.source,
    };
  }), [entries, locations, partnerTz]);

  // Re-sync every recurring squad's inherited no-class days (and its calendar
  // events) after a no-class entry changes. Sync reads the entries back from
  // Firestore, so this must run after the entry write commits.
  const propagateNoClassDays = async () => {
    const eligible = squads.filter(squadInheritsNoClassDays);
    for (const squad of eligible) {
      try {
        await syncSquadCalendarFromDetails(squad);
      } catch (syncError) {
        console.warn(`Could not update calendar for squad ${squad.id}:`, syncError);
      }
    }
  };

  const finishWrite = async (touchedNoClass: boolean, successMessage: string) => {
    if (touchedNoClass) await propagateNoClassDays();
    await onChanged();
    setCalendarRefreshKey((value) => value + 1);
    setMessage(successMessage);
    window.setTimeout(() => setMessage(null), 3500);
  };

  const saveForm = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form) return;
    const title = form.title.trim();
    if (!title || !YMD_PATTERN.test(form.startDate)) return;
    if (form.endDate && form.endDate < form.startDate) {
      setError('The end date must be on or after the start date.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const payload = {
        partnerId: partner.id,
        kind: form.kind,
        title,
        startDate: form.startDate,
        endDate: form.endDate && form.endDate !== form.startDate ? form.endDate : null,
        startTime: form.kind === 'event' && form.startTime ? form.startTime : null,
        endTime: form.kind === 'event' && form.endTime ? form.endTime : null,
        locationId: form.kind === 'event' && form.locationId ? form.locationId : null,
        excludedLocationIds: form.kind === 'no_class' ? form.excludedLocationIds : [],
        notes: form.notes.trim() || null,
      };
      if (form.id) {
        await updatePartnerCalendarEntry(partner.id, form.id, payload, actorId);
      } else {
        await createPartnerCalendarEntry(partner.id, {
          ...payload,
          source: 'manual',
          convertedSquadId: null,
          createdBy: actorId,
          updatedBy: actorId,
        }, actorId);
      }
      setForm(null);
      await finishWrite(
        form.kind === 'no_class',
        form.id ? 'Calendar entry updated.' : form.kind === 'no_class' ? 'No-class day added.' : 'Event added.',
      );
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Could not save the calendar entry.');
    } finally {
      setBusy(false);
    }
  };

  const deleteEntry = async (entry: PartnerCalendarEntry) => {
    const label = entry.kind === 'no_class' ? 'no-class day' : 'event';
    const confirmed = await window.appConfirm(`Delete the ${label} “${entry.title}”? ${entry.kind === 'no_class' ? 'Squads will get this class day back.' : 'This cannot be undone.'}`);
    if (!confirmed) return;
    setBusy(true);
    setError(null);
    try {
      await deletePartnerCalendarEntry(partner.id, entry.id);
      setSelectedEntryId(null);
      await finishWrite(entry.kind === 'no_class', `Deleted “${entry.title}”.`);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Could not delete the calendar entry.');
    } finally {
      setBusy(false);
    }
  };

  const startConvert = (entry: PartnerCalendarEntry) => {
    setConvertDraft({
      title: entry.title,
      locationId: entry.locationId || (locations.length === 1 ? locations[0].id : ''),
      singleDayType: 'class',
      demoType: undefined,
      startTime: entry.startTime || '15:30',
      endTime: entry.endTime || '16:30',
    });
    setConverting(true);
  };

  const submitConvert = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedEntry || !convertDraft || !convertDraft.title.trim() || !convertDraft.locationId) return;
    setBusy(true);
    setError(null);
    try {
      const squadId = await onConvertEventToSquad(selectedEntry, {
        ...convertDraft,
        title: convertDraft.title.trim(),
      });
      await updatePartnerCalendarEntry(partner.id, selectedEntry.id, { convertedSquadId: squadId }, actorId);
      setConverting(false);
      setConvertDraft(null);
      await finishWrite(false, `“${selectedEntry.title}” converted into a draft squad.`);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Could not convert the event into a squad.');
    } finally {
      setBusy(false);
    }
  };

  const closeModals = () => {
    if (busy) return;
    setForm(null);
    setSelectedEntryId(null);
    setConverting(false);
    setConvertDraft(null);
    setError(null);
  };

  const upcomingEntryCount = entries.filter((entry) => (entry.endDate || entry.startDate) >= currentDate).length;

  return (
    <section className="partner-calendar-section">
      <div className="partner-section-header">
        <div>
          <h2>Calendar</h2>
          <p>Class dates, partner events, and no-class days for {partner.name}. No-class days are inherited by every recurring squad unless a location is excluded.</p>
        </div>
        <div className="partner-actions partner-actions--compact">
          <button type="button" className="partner-primary-button" onClick={() => { setSelectedEntryId(null); setForm(emptyForm('event')); }}>Add event</button>
          <button type="button" onClick={() => { setSelectedEntryId(null); setForm(emptyForm('no_class')); }}>Add no-class day</button>
        </div>
      </div>

      {error && !form && !selectedEntry && (
        <div role="alert" className="partner-notice partner-notice--error"><span>{error}</span><button type="button" onClick={() => setError(null)}>Dismiss</button></div>
      )}
      {message && <div role="status" className="partner-notice partner-notice--success"><span>{message}</span></div>}

      <div className="partner-calendar-meta">
        <div className="partner-calendar-legend" aria-label="Calendar legend">
          <span><i className="partner-calendar-legend__dot partner-calendar-legend__dot--class" /> Class day</span>
          <span><i className="partner-calendar-legend__dot partner-calendar-legend__dot--event" /> Partner event</span>
          <span><i className="partner-calendar-legend__dot partner-calendar-legend__dot--no-class" /> No class</span>
        </div>
        <div className="partner-calendar-summary">
          <strong>{futureSquads.length}</strong><span>squads ending after today</span>
          <strong>{upcomingEntryCount}</strong><span>upcoming partner {upcomingEntryCount === 1 ? 'entry' : 'entries'}</span>
        </div>
      </div>

      <SquadCalendar
        squads={futureSquads}
        scopeToInitialSquads
        initialView="dayGridMonth"
        compactMonthOnly
        eventClickAction="navigate"
        onSquadOpen={onOpenSquad}
        auxEvents={auxEvents}
        onAuxEventClick={(entryId) => { setForm(null); setConverting(false); setConvertDraft(null); setSelectedEntryId(entryId); }}
        refreshKey={calendarRefreshKey}
      />
      {futureSquads.length === 0 && entries.length === 0 && (
        <div className="partner-empty-state"><strong>Nothing scheduled yet</strong><span>Squad class dates appear automatically. Add partner events and no-class days with the buttons above.</span></div>
      )}

      {form && (
        <div className="partner-cal-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeModals(); }}>
          <section className="partner-cal-modal" role="dialog" aria-modal="true" aria-labelledby="partner-cal-form-title">
            <button type="button" className="partner-cal-modal__close" aria-label="Close" disabled={busy} onClick={closeModals}>×</button>
            <h3 id="partner-cal-form-title">
              {form.id
                ? form.kind === 'no_class' ? 'Edit no-class day' : 'Edit event'
                : form.kind === 'no_class' ? 'Add no-class day' : 'Add event'}
            </h3>
            <p className="partner-section-intro">
              {form.kind === 'no_class'
                ? 'A holiday or day without programming. Every recurring squad inherits it as a no-class day unless its location is excluded below.'
                : 'Something happening at the partner worth tracking — a curriculum night, farmer’s market, book fair. Events can be converted into squads later.'}
            </p>
            {error && <div role="alert" className="partner-notice partner-notice--error"><span>{error}</span></div>}
            <form onSubmit={saveForm}>
              <label className="partner-field">
                Title
                <input
                  required
                  autoFocus
                  maxLength={120}
                  value={form.title}
                  placeholder={form.kind === 'no_class' ? 'e.g. Labor Day — no school' : 'e.g. Curriculum night'}
                  onChange={(event) => setForm({ ...form, title: event.target.value })}
                />
              </label>
              <div className="partner-field-grid">
                <label className="partner-field">
                  {form.endDate ? 'First day' : 'Date'}
                  <input required type="date" value={form.startDate} onChange={(event) => setForm({ ...form, startDate: event.target.value })} />
                </label>
                <label className="partner-field">
                  Last day <small>(optional, for multi-day)</small>
                  <input type="date" min={form.startDate || undefined} value={form.endDate} onChange={(event) => setForm({ ...form, endDate: event.target.value })} />
                </label>
              </div>
              {form.kind === 'event' && (
                <>
                  <div className="partner-field-grid">
                    <label className="partner-field">
                      Start time <small>(optional)</small>
                      <input type="time" value={form.startTime} onChange={(event) => setForm({ ...form, startTime: event.target.value })} />
                    </label>
                    <label className="partner-field">
                      End time <small>(optional)</small>
                      <input type="time" value={form.endTime} onChange={(event) => setForm({ ...form, endTime: event.target.value })} />
                    </label>
                  </div>
                  {locations.length > 0 && (
                    <label className="partner-field">
                      Location <small>(optional)</small>
                      <select value={form.locationId} onChange={(event) => setForm({ ...form, locationId: event.target.value })}>
                        <option value="">Whole partner</option>
                        {locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
                      </select>
                    </label>
                  )}
                  <label className="partner-field">
                    Notes <small>(optional)</small>
                    <textarea rows={3} value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} placeholder="Context, source link, who to talk to…" />
                  </label>
                </>
              )}
              {form.kind === 'no_class' && (
                <>
                  {locations.length > 1 && (
                    <fieldset className="partner-cal-modal__locations">
                      <legend>Applies to</legend>
                      {locations.map((location) => {
                        const excluded = form.excludedLocationIds.includes(location.id);
                        return (
                          <label key={location.id} className="partner-cal-modal__location-option">
                            <input
                              type="checkbox"
                              checked={!excluded}
                              onChange={(event) => setForm({
                                ...form,
                                excludedLocationIds: event.target.checked
                                  ? form.excludedLocationIds.filter((id) => id !== location.id)
                                  : [...form.excludedLocationIds, location.id],
                              })}
                            />
                            {location.name}
                          </label>
                        );
                      })}
                      <div className="partner-help-text">Squads at unchecked locations keep their class day.</div>
                    </fieldset>
                  )}
                  <label className="partner-field">
                    Notes <small>(optional)</small>
                    <textarea rows={2} value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} placeholder="e.g. From the district calendar" />
                  </label>
                </>
              )}
              <div className="partner-actions">
                <button type="submit" className="partner-primary-button" disabled={busy || !form.title.trim() || !form.startDate}>
                  {busy ? 'Saving…' : form.id ? 'Save changes' : form.kind === 'no_class' ? 'Add no-class day' : 'Add event'}
                </button>
                <button type="button" disabled={busy} onClick={closeModals}>Cancel</button>
              </div>
            </form>
          </section>
        </div>
      )}

      {selectedEntry && !form && (
        <div className="partner-cal-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeModals(); }}>
          <section className="partner-cal-modal" role="dialog" aria-modal="true" aria-labelledby="partner-cal-detail-title">
            <button type="button" className="partner-cal-modal__close" aria-label="Close" disabled={busy} onClick={closeModals}>×</button>
            <span className={`partner-cal-modal__eyebrow partner-cal-modal__eyebrow--${selectedEntry.kind === 'no_class' ? 'no-class' : 'event'}`}>
              {selectedEntry.kind === 'no_class' ? 'No-class day' : 'Partner event'}
              {selectedEntry.source === 'ai' && ' · added by AI'}
            </span>
            <h3 id="partner-cal-detail-title">{selectedEntry.title}</h3>
            <dl className="partner-cal-modal__facts">
              <div><dt>{selectedEntry.endDate && selectedEntry.endDate !== selectedEntry.startDate ? 'Dates' : 'Date'}</dt><dd>{formatEntryDateRange(selectedEntry)}</dd></div>
              {selectedEntry.kind === 'event' && selectedEntry.startTime && (
                <div><dt>Time</dt><dd>{formatClock(selectedEntry.startTime)}{selectedEntry.endTime ? ` – ${formatClock(selectedEntry.endTime)}` : ''}</dd></div>
              )}
              {selectedEntry.kind === 'event' && (
                <div><dt>Location</dt><dd>{locationName(selectedEntry.locationId) || 'Whole partner'}</dd></div>
              )}
              {selectedEntry.kind === 'no_class' && (
                <div>
                  <dt>Applies to</dt>
                  <dd>
                    {(selectedEntry.excludedLocationIds || []).length === 0
                      ? 'All locations'
                      : `All except ${(selectedEntry.excludedLocationIds || []).map((id) => locationName(id) || id).join(', ')}`}
                  </dd>
                </div>
              )}
              {selectedEntry.notes && <div><dt>Notes</dt><dd>{selectedEntry.notes}</dd></div>}
            </dl>
            {error && <div role="alert" className="partner-notice partner-notice--error"><span>{error}</span></div>}

            {converting && convertDraft ? (
              <form className="partner-cal-modal__convert" onSubmit={submitConvert}>
                <h4>Convert into a draft squad</h4>
                <p className="partner-section-intro">Creates a draft single-day squad on {formatEntryDate(selectedEntry.startDate)} linked to this event.</p>
                <label className="partner-field">
                  Squad title
                  <input required maxLength={100} value={convertDraft.title} onChange={(event) => setConvertDraft({ ...convertDraft, title: event.target.value })} />
                </label>
                <div className="partner-field-grid">
                  <label className="partner-field">
                    Location
                    <select required value={convertDraft.locationId} onChange={(event) => setConvertDraft({ ...convertDraft, locationId: event.target.value })}>
                      <option value="">Choose location…</option>
                      {locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
                    </select>
                  </label>
                  <label className="partner-field">
                    Kind
                    <select
                      value={convertDraft.singleDayType}
                      onChange={(event) => setConvertDraft({
                        ...convertDraft,
                        singleDayType: event.target.value as 'class' | 'demo',
                        demoType: event.target.value === 'demo' ? (convertDraft.demoType || 'event_booth') : undefined,
                      })}
                    >
                      <option value="class">Class</option>
                      <option value="demo">Demo</option>
                    </select>
                  </label>
                </div>
                {convertDraft.singleDayType === 'demo' && (
                  <label className="partner-field">
                    Demo type
                    <select value={convertDraft.demoType || 'event_booth'} onChange={(event) => setConvertDraft({ ...convertDraft, demoType: event.target.value as Squad['demoType'] })}>
                      <option value="event_booth">Booth</option>
                      <option value="lunchtime">Lunch</option>
                      <option value="school_assembly">Assembly</option>
                      <option value="classroom_visit">Classroom visit</option>
                      <option value="conference">Conference</option>
                      <option value="other">Other</option>
                    </select>
                  </label>
                )}
                <div className="partner-field-grid">
                  <label className="partner-field">
                    Start time
                    <input required type="time" value={convertDraft.startTime} onChange={(event) => setConvertDraft({ ...convertDraft, startTime: event.target.value })} />
                  </label>
                  <label className="partner-field">
                    End time
                    <input required type="time" value={convertDraft.endTime} onChange={(event) => setConvertDraft({ ...convertDraft, endTime: event.target.value })} />
                  </label>
                </div>
                <div className="partner-actions">
                  <button type="submit" className="partner-primary-button" disabled={busy || !convertDraft.title.trim() || !convertDraft.locationId}>
                    {busy ? 'Converting…' : 'Create draft squad'}
                  </button>
                  <button type="button" disabled={busy} onClick={() => { setConverting(false); setConvertDraft(null); }}>Back</button>
                </div>
              </form>
            ) : (
              <div className="partner-actions">
                {selectedEntry.kind === 'event' && (selectedEntry.convertedSquadId
                  ? <button type="button" className="partner-primary-button" onClick={() => onOpenSquad(selectedEntry.convertedSquadId as string)}>Open squad</button>
                  : <button type="button" className="partner-primary-button" disabled={busy || locations.length === 0} onClick={() => startConvert(selectedEntry)}>Convert into squad</button>)}
                <button type="button" disabled={busy} onClick={() => setForm(formFromEntry(selectedEntry))}>Edit</button>
                <button type="button" className="danger" disabled={busy} onClick={() => void deleteEntry(selectedEntry)}>{busy ? 'Working…' : 'Delete'}</button>
              </div>
            )}
            {selectedEntry.kind === 'event' && !selectedEntry.convertedSquadId && locations.length === 0 && (
              <div className="partner-help-text">Add a location to this partner before converting events into squads.</div>
            )}
          </section>
        </div>
      )}
    </section>
  );
}
